// Full Stats widget (Players canvas) — every metric for the picked player in the
// selected competition + season, grouped into the engine's categories. The
// old-Catalyst Stats tab, as a Players-canvas widget (`player-full-stats`).
// Consumes player + competition + season.
//
// Data (no backend change): the metric catalogue (`/player-metric-categories`)
// gives the grouping + each metric's outputType; one `/player-stats` row (grouped
// by player) gives the values. A Totals / Per 90 toggle swaps the metric set.
// Value formatting + key flattening live in the pure, unit-tested `fullStats.ts`.

import { useState } from "react";
import { useMetricCategories } from "../data/stats.js";
import { usePlayerStats } from "../data/players.js";
import type { WidgetProps } from "./registry.js";
import { theme } from "../lib/theme.js";
import { allMetricKeys, formatMetric } from "./fullStats.js";

export default function FullStatsWidget({ scope }: WidgetProps) {
  const { playerId, competitionId, seasonId } = scope;
  // Session-local (resets on reload) — "remember forever" is a deferred backend
  // slice (§5.3), same as FE4's stats-table config.
  const [per90, setPer90] = useState(false);

  const cats = useMetricCategories(per90);
  const keys = cats.data ? allMetricKeys(cats.data) : [];
  const stats = usePlayerStats(playerId, competitionId, seasonId, keys);

  if (playerId === null) return <Shell per90={per90} setPer90={setPer90}><Msg>Search for a player above.</Msg></Shell>;
  if (competitionId === null || seasonId === null)
    return <Shell per90={per90} setPer90={setPer90}><Msg>Pick a competition and season above.</Msg></Shell>;
  if (cats.isPending) return <Shell per90={per90} setPer90={setPer90}><Msg>Loading metrics…</Msg></Shell>;
  if (cats.isError) return <Shell per90={per90} setPer90={setPer90}><Msg danger>Couldn't load metrics. {String(cats.error)}</Msg></Shell>;
  if (stats.isPending) return <Shell per90={per90} setPer90={setPer90}><Msg>Loading stats…</Msg></Shell>;
  if (stats.isError) return <Shell per90={per90} setPer90={setPer90}><Msg danger>Couldn't load stats. {String(stats.error)}</Msg></Shell>;

  const row = stats.data.rows[0];
  if (!row) return <Shell per90={per90} setPer90={setPer90}><Msg>No stats for this player in this season.</Msg></Shell>;

  return (
    <Shell per90={per90} setPer90={setPer90}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {cats.data.map((cat) => (
          <section key={cat.id}>
            <h4 style={{ margin: "0 0 8px", color: theme.ink, fontSize: 14, fontWeight: 600 }}>{cat.name}</h4>
            {cat.subcategories.map((sub) => (
              <div key={sub.id} style={{ marginBottom: 10 }}>
                <div style={{ color: theme.inkMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
                  {sub.name}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8 }}>
                  {[...sub.playerMetrics]
                    .sort((a, b) => a.shortName.localeCompare(b.shortName))
                    .map((m) => (
                      <div
                        key={m.key}
                        title={m.definition ? `${m.name}: ${m.definition}` : m.name}
                        style={{ background: theme.field, borderRadius: 8, padding: "8px 10px" }}
                      >
                        <div
                          style={{
                            color: theme.ink,
                            fontSize: 16,
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                          }}
                        >
                          {formatMetric(row[m.key], m.outputType)}
                        </div>
                        <div style={{ color: theme.inkMuted, fontSize: 11, marginTop: 2 }}>{m.shortName}</div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </section>
        ))}
      </div>
    </Shell>
  );
}

/** The stable frame: the Totals / Per 90 toggle over a scrollable body. Keeping
 *  it around every state means the toggle never disappears while data loads. */
function Shell({
  per90,
  setPer90,
  children,
}: {
  per90: boolean;
  setPer90: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 10 }}>
      <div style={{ display: "inline-flex", alignSelf: "flex-start", background: theme.field, borderRadius: 8, padding: 2 }}>
        {([["Totals", false], ["Per 90", true]] as const).map(([label, v]) => (
          <button
            key={label}
            onClick={() => setPer90(v)}
            style={{
              border: "none",
              cursor: "pointer",
              borderRadius: 6,
              padding: "5px 12px",
              fontSize: 12,
              background: per90 === v ? theme.accentSurface : "transparent",
              color: per90 === v ? theme.accent : theme.inkMuted,
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{children}</div>
    </div>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
